import React, { useState } from 'react';
import { Tarefa, StatusTarefa } from '../../types';
import { useKanban } from '../../contexts/KanbanContext';
import { MenuItem, IconButton, Menu } from '@mui/material';
import { toast } from 'react-toastify';
import { colunasRecebidas, colunasEnviadas } from '../../data/mockData';
import tarefaService from '../../services/tarefaService';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArchiveIcon from '@mui/icons-material/Archive';
import { MIN_DIAS_UTEIS_POR_PRIORIDADE } from '../../utils/regrasPrazo';

interface KanbanCardProps {
  tarefa: Tarefa;
  usuarioLogadoId: string;
  mostrarTarefasDepartamento?: boolean;
  tipo?: 'recebidas' | 'enviadas';
}

const KanbanCard: React.FC<KanbanCardProps> = ({ tarefa, usuarioLogadoId, mostrarTarefasDepartamento = false, tipo = 'recebidas' }) => {
  const { arquivarTarefa, atualizarTarefa } = useKanban();
  const { usuario } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  
  // Verificar se a tarefa pertence ao usuário logado com base no ID passado como prop
  const tarefaDoUsuarioLogado = 
    tarefa.criador?.id === usuarioLogadoId || 
    tarefa.responsavel?.id === usuarioLogadoId;
  
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleArquivar = async () => {
    handleClose();
    try {
      await arquivarTarefa(tarefa.id);
      toast.success('Tarefa arquivada com sucesso!');
    } catch (error) {
      console.error('Erro ao arquivar tarefa:', error);
      toast.error('Erro ao arquivar tarefa. Tente novamente.');
    }
  };
  
  // Função para determinar o badge da prioridade
  const getPriorityBadge = () => {
    switch ((tarefa.prioridade || '').toLowerCase()) {
      case 'urgente':
        return 'bg-red-50 dark:bg-red-950 text-priority-urgente dark:text-red-300 border-priority-urgente';
      case 'alta':
        return 'bg-orange-50 dark:bg-orange-950 text-priority-alta border-priority-alta';
      case 'media':
        return 'bg-amber-50 dark:bg-amber-950 text-priority-media border-priority-media';
      case 'baixa':
        return 'bg-teal-50 dark:bg-teal-950 text-priority-baixa border-priority-baixa';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  // Formatar a data de vencimento
  const formatarData = (data: string | null) => {
    if (!data) return 'Sem data';
    try {
      return format(new Date(data), "dd 'de' MMM", { locale: ptBR });
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Verificar se a data de vencimento está próxima ou vencida
  const isDataVencida = () => {
    try {
      if (!tarefa.dataVencimento) return false;
      const hoje = new Date();
      const vencimento = new Date(tarefa.dataVencimento);
      return vencimento < hoje;
    } catch (error) {
      return false;
    }
  };

  // Determinar o estilo do card com base na visualização e propriedade da tarefa
  const getCardStyle = () => {
    // Se estamos na visualização de departamento e a tarefa não é do usuário logado
    if (mostrarTarefasDepartamento && !tarefaDoUsuarioLogado) {
      return 'bg-gray-50 dark:bg-gray-800/60 opacity-85'; // Fundo cinza claro e levemente transparente
    }
    return 'bg-white dark:bg-gray-800'; // Estilo padrão
  };

  // Função para obter os status disponíveis para a tarefa
  const getAvailableStatuses = (): StatusTarefa[] => {
    // Usar as colunas corretas com base no tipo (recebidas ou enviadas)
    const colunas = tipo === 'recebidas' ? colunasRecebidas : colunasEnviadas;
    
    // Status únicos a serem exibidos no dropdown (sem duplicatas)
    const statusUnicos: StatusTarefa[] = [];
    
    // Mapear sinônimos para o status principal
    const sinonimos: Record<string, string> = {
      'Solicitado': 'Pendente',
      'Em Execução': 'Em Andamento',
      'Aguardando Resposta': 'Aguardando Aprovação',
      'Finalizada': 'Concluída'
    };
    
    // Coletar apenas o status principal de cada coluna (sem sinônimos)
    colunas.forEach((coluna) => {
      // Pegar apenas o primeiro status da coluna (o principal)
      const statusPrincipal = coluna.statusPermitidos[0];
      
      // Não incluir o status atual da tarefa e evitar duplicatas
      if (!statusUnicos.includes(statusPrincipal) && statusPrincipal !== tarefa.status) {
        statusUnicos.push(statusPrincipal);
      }
    });
    
    // Ordenar os status conforme a ordem das colunas no quadro
    return statusUnicos.sort((a, b) => {
      const indexA = colunas.findIndex((col) => col.statusPermitidos.includes(a));
      const indexB = colunas.findIndex((col) => col.statusPermitidos.includes(b));
      
      if (indexA >= 0 && indexB >= 0) {
        return indexA - indexB;
      }
      
      if (indexA >= 0) return -1;
      if (indexB >= 0) return 1;
      
      return 0;
    });
  };

  // Função para lidar com a mudança de status
  const handleStatusChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const novoStatus = e.target.value as StatusTarefa;
    
    // Se o status não mudou, não fazer nada
    if (novoStatus === tarefa.status) return;
    
    setIsChangingStatus(true);
    
    try {
      // Atualizar o status da tarefa na API
      await tarefaService.atualizarStatusTarefa(tarefa.id, novoStatus);
      
      // Atualizar a tarefa no contexto com o novo status
      const tarefaAtualizada = { ...tarefa, status: novoStatus };
      await atualizarTarefa(tarefaAtualizada);

      toast.success(`Tarefa movida para ${novoStatus}`);
    } catch (error) {
      console.error('Erro ao atualizar status da tarefa:', error);
      toast.error('Erro ao mover tarefa. Tente novamente.');
    } finally {
      setIsChangingStatus(false);
    }
  };

  const getFaixaPrioridade = () => {
    switch ((tarefa.prioridade || '').toLowerCase()) {
      case 'urgente':
        return 'border-priority-urgente';
      case 'alta':
        return 'border-priority-alta';
      case 'media':
        return 'border-priority-media';
      case 'baixa':
        return 'border-priority-baixa';
      default:
        return 'border-gray-300';
    }
  };

  return (
    <div className={`${getCardStyle()} rounded-md shadow-card p-4 border-l-4 ${getFaixaPrioridade()} hover:shadow-card-hover hover:-translate-y-0.5 transition-all relative`}>
      <div className="flex justify-between items-start mb-2">
        <h4 className="font-medium text-bravo-dark dark:text-gray-100 flex-grow">{tarefa.titulo}</h4>
        <div className="flex items-center">
          <span className="text-xs font-semibold bg-bravo-tertiary dark:bg-gray-700 text-bravo-secondary dark:text-gray-300 px-2 py-1 rounded mr-1">
            #{tarefa.id.slice(-4)}
          </span>
          <IconButton
            aria-label="mais opções"
            aria-controls={open ? 'tarefa-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            onClick={handleClick}
            size="small"
            className="text-bravo-secondary dark:text-gray-300"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id="tarefa-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'tarefa-opcoes',
            }}
          >
            <MenuItem onClick={(e) => {
              e.stopPropagation(); // Impedir propagação do clique
              handleArquivar();
            }}>
              <ArchiveIcon fontSize="small" className="mr-2" />
              Arquivar
            </MenuItem>
          </Menu>
        </div>
      </div>
      
      <p className="text-sm text-bravo-secondary dark:text-gray-300 mb-3 line-clamp-2">{tarefa.descricao}</p>

      {/* Data de abertura/criação */}
      {tarefa.dataCriacao && (
        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400 mb-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Aberta em: {formatarData(tarefa.dataCriacao)}</span>
        </div>
      )}
      
      <div className="flex justify-between items-center text-xs">
        <div className={`flex items-center ${isDataVencida() ? 'text-bravo-primary' : 'text-bravo-secondary dark:text-gray-300'}`}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {formatarData(tarefa.dataVencimento)}
        </div>
        
        <div className="flex items-center text-bravo-secondary dark:text-gray-300">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
          {tarefa.comentarios?.length || 0}
        </div>
      </div>

      <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex">
          <div 
            className="w-6 h-6 rounded-full border-2 border-white bg-gray-300 flex items-center justify-center text-xs text-white"
            title={tarefa.responsavel?.nome}
          >
            {tarefa.responsavel?.nome?.charAt(0) || '?'}
          </div>
        </div>
        
        <span
          className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityBadge()}`}
          title={`Prioridade ${tarefa.prioridade} — prazo mínimo de ${MIN_DIAS_UTEIS_POR_PRIORIDADE[(tarefa.prioridade || '').toLowerCase()] ?? MIN_DIAS_UTEIS_POR_PRIORIDADE.media} dia(s) útil(eis) de antecedência`}
        >
          {tarefa.prioridade}
        </span>
      </div>

      {/* Seletor de status para facilitar mudança em monitores menores */}
      <div
        className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()} // Impedir que o clique se propague para o container do card
      >
        <div className="flex items-center justify-between">
          <span className="text-xs text-bravo-secondary dark:text-gray-300">Mover para:</span>
          <div className="flex-grow ml-2">
            <select
              className="w-full text-xs p-1 border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 text-bravo-secondary dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-bravo-primary"
              value={tarefa.status}
              onChange={(e) => {
                e.stopPropagation(); // Impedir que o evento se propague
                handleStatusChange(e);
              }}
              disabled={isChangingStatus}
            >
              {getAvailableStatuses().map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanCard;
